import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { tokens } from "../../theme";
import { PageHeader } from "../../components/PageHeader";
import {
  foyerApi,
  type OwnerBranding,
  type OwnerShell,
} from "../../http/foyer";

/**
 * How a supplier is credited (`CONTENT-20`).
 *
 * ## 🚨 The colours are gone, and that was a product decision
 *
 * This page used to set a background, an ink and an accent, which the shell
 * then wore — `CONTENT-4` had ruled the supplier surface WAS the white-label
 * storefront, so per-owner theming was a first-commit requirement. Sigge
 * withdrew that: *"I never asked for my brand - we can drop that entirely"*.
 * The content-owner pages are the Nenda Portal, full stop.
 *
 * So the theming half is deleted rather than hidden. A colour picker whose
 * output nothing renders is worse than no picker: a partner sets a brand, sees
 * no change, and sets it again.
 *
 * What survives is `identity`, which was never theming — a display name, a
 * website, credits and a copyright line are attribution that belongs to the
 * supplier whatever the page is painted with.
 *
 * ⚠️ `brand` is still on the model and the server still accepts it. Left
 * deliberately: dropping a stored field is a migration, and a studio that
 * already set colours should not have them silently erased by a redeploy. This
 * page simply no longer writes them.
 *
 * ## Owner-editable, operator read-only
 *
 * The staff route refuses these fields. An operator quietly correcting a
 * studio's copyright line is a divergence the studio cannot see.
 *
 * ## Stored, and deliberately not on a TV
 *
 * Nothing in `src/tv` renders content attribution today: which surface, and
 * which corner — two logos already want the same one and nothing arbitrates.
 * Storing what a partner tells us is honest; rendering it somewhere picked on
 * the day is not. That is its own ticket.
 */

interface ShellContext {
  shell: OwnerShell;
}

export default function SupplierBrandPage() {
  useOutletContext<ShellContext>();
  const [branding, setBranding] = useState<OwnerBranding | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    foyerApi
      .ownerBranding()
      .then((b) => live && setBranding(b))
      .catch(
        (e) => live && setError(e instanceof Error ? e.message : String(e))
      );
    return () => {
      live = false;
    };
  }, []);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!branding) return <CircularProgress size={24} />;

  const set = (identity: OwnerBranding["identity"]) => {
    setSaved(false);
    setBranding({ ...branding, identity });
  };

  async function save() {
    if (!branding) return;
    setBusy(true);
    try {
      // 🚨 `identity` only. Sending `brand` back would re-write colours this
      // page no longer shows, from state it no longer edits.
      setBranding(
        await foyerApi.saveOwnerBranding({ identity: branding.identity })
      );
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const field = (
    label: string,
    value: string | undefined,
    onChange: (next: string) => void,
    helper?: string
  ) => (
    <TextField
      label={label}
      size="small"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      helperText={helper}
      fullWidth
    />
  );

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <PageHeader
        eyebrow="Your account"
        title="How you are credited"
        subtitle="Your name and wording, shown with your titles. Only you can change this — Nenda can see it but not edit it."
      />

      <Stack spacing={2}>
        {field("Display name", branding.identity.displayName, (displayName) =>
          set({ ...branding.identity, displayName })
        )}
        {field("Website", branding.identity.websiteUrl, (websiteUrl) =>
          set({ ...branding.identity, websiteUrl })
        )}
        {field(
          "Credits",
          branding.identity.creditsText,
          (creditsText) => set({ ...branding.identity, creditsText }),
          "Shown with your titles where we credit their maker."
        )}
        {field(
          "Copyright",
          branding.identity.copyrightText,
          (copyrightText) => set({ ...branding.identity, copyrightText }),
          "Your wording, not ours to infer."
        )}
      </Stack>

      <Box>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() => void save()}
          data-testid="identity-save"
        >
          Save
        </Button>
        {saved && (
          <Typography
            component="span"
            sx={{ ml: 2, fontSize: 13, color: tokens.inkMuted }}
          >
            Saved.
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
